import dotProp from 'dot-prop-immutable';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { history } from 'react-router-prop-types';
import { bindActionCreators } from 'redux';

import JokerWizDialog from 'atg/components/dialog/JokerWizDialog';
import {
  clearCurrentBet,
  createMathematicalBet,
  fetchWizSettings,
} from 'atg/redux/actions';
import { jokerWizSelector } from 'atg/redux/selectors';
import {
  Layouts,
  MaxPrices,
  MaxPriceTypes,
  RiskLevels,
  WIZARD_STEP_CREATE_SYSTEM,
  WIZARD_STEP_HORSES,
  WIZARD_STEP_SYSTEM_TYPE,
  WizardSteps,
} from 'shared/constants/JokerWizConstants';
import { sendTrackingEvent, showErrorDialog } from 'shared/redux/actions';
import { calendar as calendarSelector } from 'shared/redux/selectors';
import { formatDateCompact } from 'shared/utils/dateUtils';

const mapStateToProps = (state, ownProps) => ({
  system: calendarSelector.getSystemById(state, ownProps.betParams.systemId),
  createdBetId: jokerWizSelector.getCreateBetId(state),
  error: jokerWizSelector.getError(state),
  isLoading:
    jokerWizSelector.isSettingsLoading(state) ||
    jokerWizSelector.isLoading(state),
  settings: jokerWizSelector.getSettings(state),
  isSettingsLoading: jokerWizSelector.isSettingsLoading(state),
  errorSettings: jokerWizSelector.getSettingsError(state),
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      clearCurrentBet,
      createMathematicalBet,
      fetchWizSettings,
      sendTrackingEvent,
      showErrorDialog,
    },
    dispatch,
  );

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(
    class JokerWizDialogContainer extends Component {
      static propTypes = {
        betParams: PropTypes.object,
        history: history.isRequired,
      };

      state = {
        customSpikes: [undefined],
        defaultSpikes: undefined,
        isLoading: false,
        layout: Layouts[1],
        maxPrice: MaxPrices[1],
        maxPriceType: MaxPriceTypes.button,
        maxSpikes: undefined,
        minSpikes: undefined,
        races: undefined,
        riskLevel: RiskLevels[1],
        wizardStepIndex: 0,
      };

      componentDidMount() {
        this.props.sendTrackingEvent('wiz', 'Opened dialog');

        // Close immediately in the rare event the Wiz Dialog opens without
        // a system, which could happen if a stale wiz systemId is in the
        // History state and the user reloads the page
        if (!this.props.system) {
          console.warn('Attempted to open Wiz without a system');
          this.props.closeHandler();
        }
      }

      componentDidUpdate(prevProps) {
        const { isLoading, error, createdBetId } = this.props;
        const { riskLevel, maxPrice } = this.state;

        if (isLoading && !prevProps.isLoading) {
          this.setState({ isLoading });
        } else if (error && !prevProps.error) {
          this.setState({ isLoading: false });
          this.props.showErrorDialog({
            message: error.statusText,
            details: '',
            logNumber: error.response?.logNumber,
          });
        } else if (createdBetId && !prevProps.createdBetId) {
          this.props.sendTrackingEvent(
            'wiz',
            'Created bet',
            riskLevel,
            maxPrice,
          );
          this.props.history.push(`/trav/${createdBetId}`);
        }
      }

      UNSAFE_componentWillReceiveProps({ isLoading, settings }) {
        this.setState({ isLoading });

        if (settings) {
          const {
            defaultSpikes,
            maxSpikes,
            minSpikes,
            races: { races },
          } = settings;

          const { customSpikes: currentCustomSpikes } = this.state;

          const customSpikes =
            defaultSpikes > 0 && currentCustomSpikes.length === 0
              ? [undefined]
              : currentCustomSpikes;

          this.setState({
            customSpikes,
            defaultSpikes,
            maxSpikes,
            minSpikes,
            races,
          });
        }
      }

      onSubmit = () => {
        const { system, createMathematicalBet } = this.props;
        const {
          customSpikes,
          defaultSpikes,
          layout,
          maxPrice,
          riskLevel,
          singleHorse,
        } = this.state;

        const { betType, date, trackKey } = system;

        const betKey = {
          betType,
          trackKey,
          date,
        };

        if (maxPrice && riskLevel) {
          const betParams = {
            betAmount: maxPrice,
            betConfig: {},
            betKey,
            customSpikes: customSpikes.filter(cs => cs != null),
            layout,
            nrOfSpikes: defaultSpikes,
            riskLevel,
            singleHorse,
          };
          createMathematicalBet(betParams);
        }
      };

      onChangeMaxPrice = (maxPrice, maxPriceType = MaxPriceTypes.button) =>
        this.setState({ maxPrice, maxPriceType });

      onChangeSpikesNr = defaultSpikes => {
        let customSpikes = [...this.state.customSpikes];

        if (
          customSpikes.length === 0 ||
          customSpikes[customSpikes.length - 1] !== undefined
        ) {
          customSpikes.push(undefined);
        }

        customSpikes = customSpikes.slice(0, defaultSpikes);

        this.setState({
          customSpikes,
          defaultSpikes,
        });
      };

      onResetCustomSpike = index =>
        this.setState({
          customSpikes: dotProp.set(this.state.customSpikes, index),
        });

      onDeleteCustomSpike = index =>
        this.setState({
          customSpikes: dotProp.delete(this.state.customSpikes, index),
        });

      onChangeCustomSpike = (index, item) =>
        this.setState({
          customSpikes: dotProp.set(this.state.customSpikes, index, item),
        });

      // { legNr, startNo }

      onAddCustomSpike = () =>
        this.setState({
          customSpikes: [...this.state.customSpikes, undefined],
        });

      onChangeLayout = layout => this.setState({ layout });

      onChangeMaxPriceField = e => {
        const { value } = e.target;

        if (Number.isNaN(value)) {
          e.preventDefault();
          return false;
        } else {
          this.onChangeMaxPrice(value === '' ? '' : +value, 'field');
        }
      };

      isMaxPriceCorrect = maxPrice => maxPrice >= 50 && maxPrice <= 100_000;

      onChangeRiskLevel = riskLevel => this.setState({ riskLevel });

      onChangeView = nextWizardStepIndex => {
        const currentWizardStep = WizardSteps[this.state.wizardStepIndex];
        const nextWizardStep = WizardSteps[nextWizardStepIndex];
        this.setState({
          wizardStepIndex: nextWizardStepIndex,
        });

        if (
          currentWizardStep === WIZARD_STEP_HORSES &&
          nextWizardStep === WIZARD_STEP_SYSTEM_TYPE
        ) {
          const { maxPrice, riskLevel } = this.state;
          const { betType, date, trackKey } = this.props.system;

          this.setState({
            customSpikes: [undefined],
            races: undefined,
          });

          this.props.fetchWizSettings({
            amount: maxPrice,
            betType,
            date: formatDateCompact(date),
            riskLevel,
            trackKey,
          });
        }

        if (nextWizardStep === WIZARD_STEP_CREATE_SYSTEM) {
          this.setState({
            layout: Layouts[1],
          });
        }
      };

      render() {
        const { system, closeHandler, isLoading, isSettingsLoading } =
          this.props;

        if (!system) {
          return null;
        }

        const {
          wizardStepIndex,
          customSpikes,
          defaultSpikes,
          layout,
          maxPrice,
          maxPriceType,
          maxSpikes,
          minSpikes,
          races,
          riskLevel,
          singleHorse,
        } = this.state;

        const isMaxPriceCorrect = this.isMaxPriceCorrect(maxPrice);
        const disableSubmit = {
          0: !isMaxPriceCorrect || !riskLevel,
          1: isSettingsLoading,
          2: false,
        };

        return (
          <JokerWizDialog
            customSpikes={customSpikes}
            defaultSpikes={defaultSpikes}
            disableSubmit={disableSubmit[wizardStepIndex]}
            isLoading={isLoading}
            layout={layout}
            minSpikes={minSpikes}
            maxPrice={maxPrice}
            maxPriceType={maxPriceType}
            maxSpikes={maxSpikes}
            races={races}
            riskLevel={riskLevel}
            singleHorse={singleHorse}
            stepIndex={wizardStepIndex}
            system={system}
            onAddCustomSpike={this.onAddCustomSpike}
            onChangeMaxPrice={this.onChangeMaxPrice}
            onChangeMaxPriceField={this.onChangeMaxPriceField}
            onChangeCustomSpike={this.onChangeCustomSpike}
            onChangeRiskLevel={this.onChangeRiskLevel}
            onChangeSpikesNr={this.onChangeSpikesNr}
            onChangeLayout={this.onChangeLayout}
            onChangeView={this.onChangeView}
            onClose={closeHandler}
            onDeleteCustomSpike={this.onDeleteCustomSpike}
            onResetCustomSpike={this.onResetCustomSpike}
            onSubmit={this.onSubmit}
          />
        );
      }
    },
  ),
);
